import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.LinearOutSlowInEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.animateIntAsState
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.spring
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Surface
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.unit.TextUnit
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.scale
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.unit.times
import dev.gitlive.firebase.firestore.Timestamp
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import models.BustOdds
import models.DomainGame
import models.GameAnalyticsCalculator
import models.GameSettings
import models.GinkoEvent
import models.Setting
import models.Round
import models.isPublicListingFresh
import models.sevenLeaders
import kotlin.math.sin
import kotlin.random.Random
import org.jetbrains.compose.resources.stringResource
import banko.composeapp.generated.resources.Res
import banko.composeapp.generated.resources.*

@Composable
internal fun GameContentSection(
    isRolling: MutableState<Boolean>,
    game: DomainGame?,
    gameState: GameScreenState,
    uiState: BankoGameScreenUiState,
    isWideLayout: Boolean = false,
    isPhoneLandscapeLayout: Boolean = false,
    // A side-by-side pane with plenty of height (a foldable's book page): the phone-landscape
    // structure, but the table keeps its full size and centers instead of shrinking to the top.
    isTallPane: Boolean = false,
    shouldShowManualDiceGrid: Boolean = false,
    showPlayerPickerDialog: MutableState<Boolean>? = null,
    onFinishGame: (() -> Unit)? = null,
    modifier: Modifier = Modifier,
) {
    val isActiveGame = gameState.isStarted && !gameState.isGameOver
    if (isActiveGame) {
        // The play cluster (dice / pot / roll button) centers in the space below the player grid when
        // it fits, so it doesn't huddle at the top with dead space beneath it. But the content can
        // exceed that space — a Ginko event banner adds a row, and short screens are tight — so it
        // scrolls rather than squashing the pot and dropping the turn indicator / roll button off the
        // bottom. heightIn(min) keeps it centered until it overflows, then the scroll takes over.
        BoxWithConstraints(modifier = modifier.fillMaxWidth()) {
            val minContentHeight = maxHeight
            // Shrink the dice/pot table so the whole cluster (through the turn indicator) fits the
            // available height. The constants model the cluster: ~130dp of fixed chrome (round chip,
            // turn indicator, pot label — the roll button now lives in the pinned bottom sheet)
            // plus ~320dp of scalable height (dice, pot number, table padding, surrounding
            // spacers). On roomy screens this clamps to 1f, so the full-size design is untouched;
            // it only shrinks when the cluster would otherwise overflow and force a scroll. The
            // verticalScroll below is the safety net for the most extreme cases (e.g. a Ginko
            // event banner on a very short screen).
            val playScale = when {
                // Leaves room for the Roll / ¡Banko! cluster, which shares this pane.
                isTallPane -> ((maxHeight - 230.dp) / 330.dp).coerceIn(0.5f, 1f)
                isPhoneLandscapeLayout -> ((maxHeight - 96.dp) / 340.dp).coerceIn(0.30f, 0.44f)
                isWideLayout -> ((maxHeight - 110.dp) / 360.dp).coerceIn(0.88f, 1.16f)
                else -> ((maxHeight - 130.dp) / 320.dp).coerceIn(0.6f, 1f)
            }
            val topAligned = isPhoneLandscapeLayout && !isTallPane
            Column(
                verticalArrangement = if (topAligned) Arrangement.Top else Arrangement.Center,
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier
                    .fillMaxWidth()
                    .offset(y = if (topAligned) (-8).dp else 0.dp)
                    .verticalScroll(rememberScrollState())
                    .heightIn(min = if (topAligned) 0.dp else minContentHeight),
            ) {
                ActiveGameContent(
                    game = game!!,
                    gameState = gameState,
                    uiState = uiState,
                    isRolling = isRolling,
                    isWideLayout = isWideLayout,
                    isPhoneLandscapeLayout = isPhoneLandscapeLayout,
                    shouldShowManualDiceGrid = shouldShowManualDiceGrid,
                    showPlayerPickerDialog = showPlayerPickerDialog,
                    scale = playScale,
                )
            }
        }
    } else {
        // Pre-game and game-over use a top-aligned layout; pre-game scrolls internally so its
        // settings list never gets clipped.
        Box(
            modifier = modifier.fillMaxSize(),
            contentAlignment = Alignment.TopCenter,
        ) {
            if (!gameState.isStarted) {
                PreGameContent(
                    game = game,
                    gameState = gameState,
                    uiState = uiState,
                    isPhoneLandscapeLayout = isPhoneLandscapeLayout,
                )
            } else {
                GameOverContent(
                    game = game!!,
                    selfName = uiState.playerName,
                    compactLandscape = isPhoneLandscapeLayout,
                    isHost = gameState.isHost,
                    onRematch = uiState.onRematch,
                    onFinishGame = onFinishGame,
                    modifier = Modifier.fillMaxSize(),
                )
            }
        }
    }
}

@Composable
private fun PreGameContent(
    game: DomainGame?,
    gameState: GameScreenState,
    uiState: BankoGameScreenUiState,
    isPhoneLandscapeLayout: Boolean = false,
) {
    val purchaseState by RemoveAdsPurchases.uiState.collectAsState()
    var showPlusUpsell by remember { mutableStateOf(false) }
    val isNetworkGame = uiState.gameCode != "LOCAL"
    val filteredBooleanSettings = visibleLobbySettings(game?.settings ?: emptyList(), isNetworkGame)
    val gameLengthPremiumUnlocked = isGameLengthPremiumUnlocked(
        isHost = gameState.isHost,
        hostPerksActive = game?.hostPerksActive == true,
        bankoPlusActive = purchaseState.bankoPlusActive,
    )

    LazyColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(
            start = if (isPhoneLandscapeLayout) 8.dp else 16.dp,
            end = if (isPhoneLandscapeLayout) 8.dp else 16.dp,
            top = if (isPhoneLandscapeLayout) 0.dp else 4.dp,
            bottom = if (isPhoneLandscapeLayout) 12.dp else 112.dp,
        ),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(if (isPhoneLandscapeLayout) 6.dp else 8.dp),
    ) {
        if (!gameState.isHost) {
            item {
                WaitingForHost()
            }
        }

        if (isNetworkGame) {
            item {
                PublicGameVisibilityCard(
                    isPublic = game?.isPublic == true,
                    publicSinceSeconds = game?.publicSinceSeconds,
                    isHost = gameState.isHost,
                    onVisibilityChanged = uiState.onPublicVisibilityChanged,
                )
            }
        }

        item {
            Text(
                text = stringResource(Res.string.game_settings),
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.onBackground,
                modifier = Modifier.fillMaxWidth(),
                textAlign = TextAlign.Center,
            )
        }

        if (isPhoneLandscapeLayout && gameState.isHost) {
            item {
                StartGameHostAction(
                    playerCount = game?.players?.size ?: 0,
                    buttonHeight = 52.dp,
                    onStartGame = uiState.onStartGame,
                    modifier = Modifier.fillMaxWidth(),
                )
            }
        }

        items(filteredBooleanSettings) { setting ->
            GameSettingToggleCard(
                setting = setting,
                onSettingChanged = uiState.onSettingChanged,
                isHost = gameState.isHost,
            )
        }

        item {
            GameLengthSetting(
                endRoundNum = game?.endRoundNum ?: DEFAULT_ROUNDS,
                premiumUnlocked = gameLengthPremiumUnlocked,
                isHost = gameState.isHost,
                onEndRoundNumChanged = uiState.onEndRoundNumChanged,
                onUpgrade = if (purchaseState.isPurchasesSupported) {
                    { showPlusUpsell = true }
                } else {
                    null
                },
            )
        }

    }

    if (showPlusUpsell) {
        BankoStoreSheet(onDismiss = { showPlusUpsell = false }, plusOnly = true)
    }
}

@Composable
private fun PublicGameVisibilityCard(
    isPublic: Boolean,
    publicSinceSeconds: Long?,
    isHost: Boolean,
    onVisibilityChanged: (Boolean) -> Unit,
) {
    var showPublicWarning by remember { mutableStateOf(false) }
    var nowSeconds by remember(isPublic, publicSinceSeconds) { mutableStateOf(Timestamp.now().seconds) }
    LaunchedEffect(isPublic, publicSinceSeconds) {
        while (isPublic) {
            nowSeconds = Timestamp.now().seconds
            delay(PUBLIC_LISTING_STATUS_REFRESH_MS)
        }
    }
    val listingStale = isPublic && !isPublicListingFresh(publicSinceSeconds, nowSeconds)
    val requestChange: (Boolean) -> Unit = { makePublic ->
        if (makePublic) showPublicWarning = true else onVisibilityChanged(false)
    }

    Surface(
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.surfaceContainerHigh,
        shadowElevation = 2.dp,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        modifier = Modifier
            .fillMaxWidth()
            .then(
                if (isHost && !listingStale) Modifier.clickable { requestChange(!isPublic) }
                else Modifier
            ),
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            Icon(
                imageVector = BankoIcons.Lock,
                contentDescription = null,
                tint = when {
                    listingStale && isHost -> MaterialTheme.colorScheme.error
                    isPublic -> MaterialTheme.colorScheme.primary
                    else -> MaterialTheme.colorScheme.onSurfaceVariant
                },
            )
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(Res.string.who_can_join),
                    style = MaterialTheme.typography.labelLarge,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Text(
                    text = stringResource(
                        when {
                            listingStale && isHost -> Res.string.public_game_stale
                            isPublic -> Res.string.public_game
                            else -> Res.string.invite_only
                        }
                    ),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                )
                Text(
                    text = stringResource(
                        when {
                            listingStale && isHost -> Res.string.public_game_stale_description
                            isPublic -> Res.string.public_game_description
                            else -> Res.string.invite_only_description
                        }
                    ),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                if (listingStale && isHost) {
                    TextButton(
                        onClick = { onVisibilityChanged(true) },
                        contentPadding = PaddingValues(horizontal = 0.dp, vertical = 2.dp),
                    ) {
                        Text(stringResource(Res.string.refresh_public_listing))
                    }
                }
            }
            Switch(
                checked = isPublic,
                onCheckedChange = if (isHost) requestChange else null,
            )
        }
    }

    if (showPublicWarning) {
        AlertDialog(
            onDismissRequest = { showPublicWarning = false },
            title = { Text(stringResource(Res.string.make_game_public_title)) },
            text = { Text(stringResource(Res.string.make_game_public_warning)) },
            confirmButton = {
                Button(
                    onClick = {
                        showPublicWarning = false
                        onVisibilityChanged(true)
                    },
                ) {
                    Text(stringResource(Res.string.make_public))
                }
            },
            dismissButton = {
                TextButton(onClick = { showPublicWarning = false }) {
                    Text(stringResource(Res.string.cancel))
                }
            },
        )
    }
}

private const val PUBLIC_LISTING_STATUS_REFRESH_MS = 30_000L

@Composable
private fun WaitingForHost() {
    Surface(
        shape = RoundedCornerShape(20.dp),
        color = MaterialTheme.colorScheme.surfaceVariant,
        modifier = Modifier.fillMaxWidth(),
    ) {
        Row(
            modifier = Modifier.padding(20.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            CircularProgressIndicator(
                modifier = Modifier.size(28.dp),
                strokeWidth = 3.dp,
                color = MaterialTheme.colorScheme.primary,
            )
            Text(
                text = stringResource(Res.string.waiting_for_host_to_start_game),
                style = MaterialTheme.typography.bodyLarge,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

@Composable
private fun GameSettingToggleCard(
    setting: Setting,
    onSettingChanged: (Setting) -> Unit = {},
    isHost: Boolean = false,
) {
    val checked = setting.value.toBoolean()
    val toggle = { onSettingChanged(Setting(setting.name, (!checked).toString())) }
    Surface(
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.surfaceContainerHigh,
        shadowElevation = 2.dp,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        modifier = Modifier.fillMaxWidth(),
    ) {
        Row(
            modifier = Modifier
                .clickable(enabled = isHost) { toggle() }
                .padding(horizontal = 16.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = GameSettings.displayName(setting),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                val description = GameSettings.description(setting)
                if (description.isNotEmpty()) {
                    Text(
                        text = description,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.75f),
                    )
                }
            }
            Spacer(modifier = Modifier.width(12.dp))
            Switch(
                checked = checked,
                onCheckedChange = { toggle() },
                enabled = isHost,
            )
        }
    }
}

private const val DEFAULT_ROUNDS = 10
private const val MIN_ROUNDS = 3
private const val MAX_ROUNDS = 20


/**
 * Custom game-length control. Editable only by a host whose lobby has Banko Plus ([premiumUnlocked]);
 * otherwise it shows a "Plus" badge and an upsell caption. Non-Plus lobbies just play the default
 * length, so the stepper is hidden rather than disabled to keep the teaser clean.
 */
@Composable
private fun GameLengthSetting(
    endRoundNum: Int,
    premiumUnlocked: Boolean,
    isHost: Boolean,
    onEndRoundNumChanged: (Int) -> Unit,
    onUpgrade: (() -> Unit)? = null,
) {
    // The host can tap the locked row to open the Banko Plus upsell that grants custom game length.
    val upsellClickable = !premiumUnlocked && isHost && onUpgrade != null
    Surface(
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.surfaceContainerHigh,
        shadowElevation = 2.dp,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        modifier = Modifier
            .fillMaxWidth()
            .then(if (upsellClickable) Modifier.clickable { onUpgrade() } else Modifier),
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    Text(
                        text = stringResource(Res.string.game_length),
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.SemiBold,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                    if (!premiumUnlocked) PlusBadge()
                }
                Text(
                    text = if (premiumUnlocked) {
                        stringResource(Res.string.rounds_format, endRoundNum)
                    } else {
                        stringResource(Res.string.banko_plus_unlocks_game_length)
                    },
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.75f),
                )
            }
            if (premiumUnlocked && isHost) {
                Spacer(modifier = Modifier.width(12.dp))
                IconButton(
                    onClick = { onEndRoundNumChanged((endRoundNum - 1).coerceAtLeast(MIN_ROUNDS)) },
                    enabled = endRoundNum > MIN_ROUNDS,
                ) {
                    Text(text = "−", style = MaterialTheme.typography.titleLarge)
                }
                Text(
                    text = endRoundNum.toString(),
                    style = MaterialTheme.typography.titleMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                IconButton(
                    onClick = { onEndRoundNumChanged((endRoundNum + 1).coerceAtMost(MAX_ROUNDS)) },
                    enabled = endRoundNum < MAX_ROUNDS,
                ) {
                    Text(text = "+", style = MaterialTheme.typography.titleLarge)
                }
            }
        }
    }
}

@Composable
private fun PlusBadge() {
    Surface(
        shape = RoundedCornerShape(percent = 50),
        color = MaterialTheme.colorScheme.primary,
    ) {
        Text(
            text = stringResource(Res.string.banko_plus_badge),
            style = MaterialTheme.typography.labelSmall,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.onPrimary,
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp),
        )
    }
}

@Composable
private fun ActiveGameContent(
    game: DomainGame,
    gameState: GameScreenState,
    uiState: BankoGameScreenUiState,
    // True while the dice roll animation is playing, so the roll button stays disabled until it
    // finishes — prevents queuing another roll mid-animation. Hoisted: the button lives in the
    // pinned bottom sheet while the animation that drives this lives here.
    isRolling: MutableState<Boolean>,
    isWideLayout: Boolean = false,
    isPhoneLandscapeLayout: Boolean = false,
    shouldShowManualDiceGrid: Boolean = false,
    showPlayerPickerDialog: MutableState<Boolean>? = null,
    // 1f on roomy screens; shrinks the dice/pot table (and the spacing around it) on short screens so
    // the turn indicator stays on-screen without scrolling.
    scale: Float = 1f,
) {

    // Round chip — a raised pill with a hairline rim, matching the elevated play surfaces below —
    // with the live bust risk beside it, so nobody has to know the rules by heart to read the table.
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Surface(
            shape = RoundedCornerShape(percent = 50),
            color = MaterialTheme.colorScheme.surfaceContainerHighest,
            shadowElevation = 2.dp,
            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        ) {
            Text(
                text = stringResource(Res.string.round_format, game.round?.roundNum ?: 0, game.endRoundNum),
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.SemiBold,
                letterSpacing = 0.5.sp,
                modifier = Modifier.padding(horizontal = 18.dp, vertical = 7.dp),
            )
        }
        game.round?.let { round -> BustRiskChip(round) }
    }

    Spacer(
        modifier = Modifier.height(
            when {
                isPhoneLandscapeLayout -> 4.dp
                isWideLayout -> 10.dp * scale
                else -> 16.dp * scale
            }
        )
    )

    if (isPhoneLandscapeLayout && showPlayerPickerDialog != null) {
        WideActionCluster(
            uiState = uiState,
            game = game,
            gameState = gameState,
            isRolling = isRolling.value,
            shouldShowManualDiceGrid = shouldShowManualDiceGrid,
            showPlayerPickerDialog = showPlayerPickerDialog,
        )
        Spacer(modifier = Modifier.height(6.dp))
    }

    // A subtly raised "table" panel behind the dice and pot so the play area reads as a surface
    // instead of floating in the flat background. Uses a non-clipping background (not a Surface) so
    // a die's fire-hat can still rise above the panel's top edge.
    val tableShape = RoundedCornerShape(if (isPhoneLandscapeLayout) 22.dp else 28.dp)
    val tableModifier = if (isWideLayout || isPhoneLandscapeLayout) {
        Modifier
            .widthIn(max = 760.dp)
            .fillMaxWidth()
    } else {
        Modifier
            .fillMaxWidth()
            .padding(horizontal = 20.dp)
    }
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = tableModifier
            .background(
                brush = Brush.verticalGradient(
                    listOf(
                        MaterialTheme.colorScheme.surfaceContainerHigh,
                        MaterialTheme.colorScheme.surfaceContainer,
                    ),
                ),
                shape = tableShape,
            )
            .border(1.dp, MaterialTheme.colorScheme.outlineVariant, tableShape)
            .padding(
                horizontal = if (isPhoneLandscapeLayout) 14.dp else if (isWideLayout) 24.dp else 0.dp,
                vertical = if (isPhoneLandscapeLayout) 10.dp * scale else if (isWideLayout) 20.dp * scale else 24.dp * scale,
            ),
    ) {
        Box(
            modifier = Modifier.fillMaxWidth(),
            contentAlignment = Alignment.Center,
        ) {
            DiceSectionWithAnimation(
                game = game,
                onRollingChange = { isRolling.value = it },
                dieSize = DIE_SIZE * scale,
            )
            TableReactionOverlay(
                uiState = uiState,
                modifier = Modifier
                    .align(Alignment.TopCenter)
                    .offset(y = (-8).dp),
            )
        }

        Spacer(modifier = Modifier.height(12.dp * scale))

        val activeGinkoEvent = game.round?.ginkoEvent ?: GinkoEvent.NONE
        PotDisplay(
            points = game.round?.currentPoints ?: 0,
            hidden = activeGinkoEvent == GinkoEvent.MYSTERY_SURGE,
            scale = scale,
        )

        if (activeGinkoEvent != GinkoEvent.NONE) {
            Spacer(modifier = Modifier.height(12.dp))
            GinkoEventBanner(activeGinkoEvent)
        }
    }

    Spacer(
        modifier = Modifier.height(
            when {
                isPhoneLandscapeLayout -> 6.dp
                isWideLayout -> 12.dp * scale
                else -> 16.dp * scale
            }
        )
    )

    val nudgingEnabled = GameSettings.isEnabled(game.settings, GameSettings.Nudging)
    // Bots and host-created players play on the host's device — there's no remote device to buzz,
    // so nudging them is meaningless.
    val currentTurnsPlayer = gameState.currentTurnsPlayer
    val showNudgeAction = uiState.gameCode != "LOCAL" &&
            nudgingEnabled &&
            !gameState.selfIsCurrentTurnsPlayer &&
            currentTurnsPlayer != null &&
            !currentTurnsPlayer.isBot &&
            !currentTurnsPlayer.hostCreated
    val canNudge by uiState.canNudgeFlow.collectAsState()

    TurnIndicator(
        isYourTurn = gameState.selfIsCurrentTurnsPlayer,
        currentPlayerName = gameState.currentTurnsPlayer?.name ?: "",
        showNudgeAction = showNudgeAction,
        canNudge = canNudge,
        onNudge = { uiState.onNudge() },
    )

}

/**
 * How risky the next roll is: "Safe roll · N left" through the opening rolls, then the chance the
 * next roll busts the pot (17% normally; more under a Danger Zone). Icon + words, never color alone.
 */
@Composable
private fun BustRiskChip(round: Round) {
    val risk = remember(round.currentRoll, round.modifier, round.ginkoEvent) { BustOdds.forRound(round) }
    val description = stringResource(Res.string.bust_risk_description)
    Surface(
        shape = RoundedCornerShape(percent = 50),
        color = if (risk.safe) {
            MaterialTheme.colorScheme.tertiaryContainer
        } else {
            MaterialTheme.colorScheme.errorContainer
        },
        contentColor = if (risk.safe) {
            MaterialTheme.colorScheme.onTertiaryContainer
        } else {
            MaterialTheme.colorScheme.onErrorContainer
        },
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        modifier = Modifier.semantics { contentDescription = description },
    ) {
        Text(
            text = if (risk.safe) {
                "🛡 " + stringResource(Res.string.bust_risk_safe, risk.safeRollsLeft)
            } else {
                "⚠ " + stringResource(Res.string.bust_risk_live, "${risk.bustChancePercent}%")
            },
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.SemiBold,
            maxLines = 1,
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 7.dp),
        )
    }
}

@Composable
private fun WideActionCluster(
    uiState: BankoGameScreenUiState,
    game: DomainGame,
    gameState: GameScreenState,
    isRolling: Boolean,
    shouldShowManualDiceGrid: Boolean,
    showPlayerPickerDialog: MutableState<Boolean>,
) {
    val round = game.round ?: return

    Column(
        modifier = Modifier
            .widthIn(max = 760.dp)
            .fillMaxWidth()
            .padding(horizontal = 6.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            val buttonHeight = 46.dp
            if (uiState.gameCode != "LOCAL" && !gameState.canActOnCurrentTurn) {
                TableReactionActionButton(
                    uiState = uiState,
                    fontSize = 16.sp,
                    modifier = Modifier.weight(1f).height(buttonHeight),
                )
            } else {
                GlossyPrimaryButton(
                    text = stringResource(Res.string.roll_dice),
                    onClick = { uiState.onDiceRolled(null) },
                    enabled = gameState.canActOnCurrentTurn && !isRolling,
                    leadingIcon = BankoIcons.Casino,
                    fontSize = 16.sp,
                    modifier = Modifier.weight(1f).height(buttonHeight),
                )
            }
            BankoActionButton(
                uiState = uiState,
                game = game,
                gameState = gameState,
                showPlayerPickerDialog = showPlayerPickerDialog,
                modifier = Modifier.weight(1f).height(buttonHeight),
                fontSize = 16.sp,
            )
        }

        if (shouldShowManualDiceGrid) {
            FourByThreeBoxGrid(
                rollNumber = round.currentRoll,
                onManuallyEnteredDice = uiState.onDiceRolled,
            )
        }
    }
}

/**
 * The app's primary action button: a sheened gold gradient face (light top → shaded bottom) over
 * the theme primary, with elevation that presses on tap. Shared by Roll Dice, ¡Banko!, and Start
 * Game so every primary action carries the same tactile, glossy treatment.
 */
@Composable
internal fun GlossyPrimaryButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    loading: Boolean = false,
    fontSize: TextUnit = 18.sp,
    leadingIcon: ImageVector? = null,
    shape: Shape = RoundedCornerShape(16.dp),
) {
    GlossyButton(
        text = text,
        onClick = onClick,
        containerColor = MaterialTheme.colorScheme.primary,
        contentColor = MaterialTheme.colorScheme.onPrimary,
        modifier = modifier,
        enabled = enabled,
        loading = loading,
        fontSize = fontSize,
        leadingIcon = leadingIcon,
        shape = shape,
    )
}

/**
 * Secondary sibling of [GlossyPrimaryButton]: the same glossy face, shape, and elevation but in the
 * muted secondary container color, so alternative actions (e.g. Find a Game) belong to the same
 * button family while staying visually subordinate to the primary call to action.
 */
@Composable
internal fun GlossySecondaryButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    loading: Boolean = false,
    fontSize: TextUnit = 18.sp,
    leadingIcon: ImageVector? = null,
    shape: Shape = RoundedCornerShape(16.dp),
) {
    GlossyButton(
        text = text,
        onClick = onClick,
        containerColor = MaterialTheme.colorScheme.secondaryContainer,
        contentColor = MaterialTheme.colorScheme.onSecondaryContainer,
        modifier = modifier,
        enabled = enabled,
        loading = loading,
        fontSize = fontSize,
        leadingIcon = leadingIcon,
        shape = shape,
    )
}

@Composable
private fun GlossyButton(
    text: String,
    onClick: () -> Unit,
    containerColor: Color,
    contentColor: Color,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    loading: Boolean = false,
    fontSize: TextUnit = 18.sp,
    leadingIcon: ImageVector? = null,
    shape: Shape = RoundedCornerShape(16.dp),
) {
    val baseColor = if (enabled) {
        containerColor
    } else {
        containerColor.copy(alpha = 0.4f)
    }
    val interactionSource = remember { MutableInteractionSource() }
    Button(
        onClick = onClick,
        enabled = enabled && !loading,
        interactionSource = interactionSource,
        shape = shape,
        contentPadding = PaddingValues(),
        colors = ButtonDefaults.buttonColors(
            containerColor = Color.Transparent,
            disabledContainerColor = Color.Transparent,
            contentColor = contentColor,
            disabledContentColor = contentColor.copy(alpha = 0.7f),
        ),
        elevation = ButtonDefaults.buttonElevation(
            defaultElevation = 6.dp,
            pressedElevation = 2.dp,
        ),
        modifier = modifier.pressScale(interactionSource),
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(baseColor)
                .background(
                    Brush.verticalGradient(
                        0f to Color.White.copy(alpha = 0.22f),
                        0.5f to Color.Transparent,
                        1f to Color.Black.copy(alpha = 0.14f),
                    ),
                ),
            contentAlignment = Alignment.Center,
        ) {
            if (loading) {
                CircularProgressIndicator(
                    modifier = Modifier.size(24.dp),
                    color = contentColor,
                    strokeWidth = 2.dp,
                )
            } else {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    if (leadingIcon != null) {
                        Icon(
                            imageVector = leadingIcon,
                            contentDescription = null,
                            modifier = Modifier.size(22.dp),
                        )
                        Spacer(modifier = Modifier.width(8.dp))
                    }
                    Text(
                        text = text,
                        fontSize = fontSize,
                        fontWeight = FontWeight.SemiBold,
                    )
                }
            }
        }
    }
}

@Composable
private fun PotDisplay(points: Int, hidden: Boolean, scale: Float = 1f) {
    // Count up/down to the new pot so banking and busting feel weighty.
    val animatedPoints by animateIntAsState(
        targetValue = points,
        animationSpec = tween(durationMillis = 600, easing = LinearEasing),
    )
    // Pop the coin each time the pot grows — a quick punch-out, then a bouncy settle.
    val popScale = remember { Animatable(1f) }
    var previousPoints by remember { mutableStateOf(points) }
    LaunchedEffect(points, hidden) {
        if (!hidden && points > previousPoints) {
            popScale.snapTo(1f)
            popScale.animateTo(1.16f, tween(durationMillis = 110))
            popScale.animateTo(
                targetValue = 1f,
                animationSpec = spring(
                    dampingRatio = Spring.DampingRatioMediumBouncy,
                    stiffness = Spring.StiffnessMedium,
                ),
            )
        }
        previousPoints = points
    }
    val potShape = RoundedCornerShape(22.dp)
    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        Text(
            text = if (hidden) stringResource(Res.string.ginko_mystery_pot_label) else "POT",
            fontFamily = theme.bankoDisplayFontFamily(),
            fontSize = 13.sp,
            color = MaterialTheme.colorScheme.primary,
            letterSpacing = if (hidden) 1.sp else 3.sp,
        )
        Spacer(modifier = Modifier.height(6.dp))
        Box(
            modifier = Modifier
                .scale(popScale.value)
                // Gold spot-shadow reads as a soft glow, so the pot feels like it's lit from within.
                .shadow(
                    elevation = 12.dp,
                    shape = potShape,
                    spotColor = MaterialTheme.colorScheme.primary,
                    ambientColor = MaterialTheme.colorScheme.primary,
                )
                .clip(potShape)
                .background(MaterialTheme.colorScheme.primaryContainer),
            contentAlignment = Alignment.Center,
        ) {
            // Glossy top highlight, matching the dice — a struck-coin sheen.
            Box(
                Modifier
                    .matchParentSize()
                    .background(
                        brush = Brush.verticalGradient(
                            0f to Color.White.copy(alpha = 0.40f),
                            0.5f to Color.Transparent,
                            1f to Color.Black.copy(alpha = 0.08f),
                        ),
                        shape = potShape,
                    ),
            )
            Text(
                text = if (hidden) "???" else animatedPoints.toString(),
                fontFamily = theme.bankoDisplayFontFamily(),
                fontSize = (if (hidden) 48f else 56f).times(scale).sp,
                color = MaterialTheme.colorScheme.onPrimaryContainer,
                // Luckiest Guy reserves descender space below the baseline that digits never use, so
                // it visually sits high. Compensate with extra top / less bottom padding to center it.
                modifier = Modifier
                    .widthIn(min = 136.dp * scale)
                    .padding(
                        start = 40.dp * scale,
                        end = 40.dp * scale,
                        top = 26.dp * scale,
                        bottom = 8.dp * scale,
                    ),
                textAlign = TextAlign.Center,
            )
        }
    }
}

@Composable
private fun GinkoEventBanner(event: GinkoEvent) {
    Surface(
        shape = RoundedCornerShape(12.dp),
        color = MaterialTheme.colorScheme.tertiaryContainer,
        contentColor = MaterialTheme.colorScheme.onTertiaryContainer,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier.padding(horizontal = 14.dp, vertical = 8.dp),
        ) {
            Text(
                text = ginkoEventTitle(event),
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.Bold,
                letterSpacing = 0.5.sp,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Text(
                text = ginkoEventDescription(event),
                style = MaterialTheme.typography.labelMedium,
                textAlign = TextAlign.Center,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

@Composable
private fun ginkoEventTitle(event: GinkoEvent): String = when (event) {
    GinkoEvent.NONE -> ""
    GinkoEvent.DANGER_ZONE -> stringResource(Res.string.ginko_event_danger_zone_title)
    GinkoEvent.HEIST_WINDOW -> stringResource(Res.string.ginko_event_heist_window_title)
    GinkoEvent.MYSTERY_SURGE -> stringResource(Res.string.ginko_event_mystery_surge_title)
    GinkoEvent.LAST_CALL -> stringResource(Res.string.ginko_event_last_call_title)
    GinkoEvent.ALL_IN -> stringResource(Res.string.ginko_event_all_in_title)
    GinkoEvent.HOT_STREAK -> stringResource(Res.string.ginko_event_hot_streak_title)
}

@Composable
private fun ginkoEventDescription(event: GinkoEvent): String = when (event) {
    GinkoEvent.NONE -> ""
    GinkoEvent.DANGER_ZONE -> stringResource(Res.string.ginko_event_danger_zone_desc)
    GinkoEvent.HEIST_WINDOW -> stringResource(Res.string.ginko_event_heist_window_desc)
    GinkoEvent.MYSTERY_SURGE -> stringResource(Res.string.ginko_event_mystery_surge_desc)
    GinkoEvent.LAST_CALL -> stringResource(Res.string.ginko_event_last_call_desc)
    GinkoEvent.ALL_IN -> stringResource(Res.string.ginko_event_all_in_desc)
    GinkoEvent.HOT_STREAK -> stringResource(Res.string.ginko_event_hot_streak_desc)
}

@Composable
private fun TurnIndicator(
    isYourTurn: Boolean,
    currentPlayerName: String,
    showNudgeAction: Boolean,
    canNudge: Boolean,
    onNudge: () -> Unit,
) {
    val transition = rememberInfiniteTransition(label = "turnPulse")
    val pulse by transition.animateFloat(
        initialValue = 1f,
        targetValue = 1.22f,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = 700, easing = LinearEasing),
            repeatMode = RepeatMode.Reverse,
        ),
        label = "pulseScale",
    )

    val contentColor =
        if (isYourTurn) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant
    val dotColor =
        if (isYourTurn) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.outline
    val label =
        if (isYourTurn) stringResource(Res.string.your_turn)
        else stringResource(Res.string.player_turn, currentPlayerName)
    val nudgeLabel = stringResource(Res.string.nudge_button)
    val nudgeVisibility by animateFloatAsState(
        targetValue = if (showNudgeAction) 1f else 0f,
        animationSpec = tween(durationMillis = if (showNudgeAction) 180 else 120),
        label = "nudgeVisibility",
    )

    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier
            .fillMaxWidth()
            .height(40.dp)
            .padding(horizontal = 24.dp),
    ) {
        Spacer(modifier = Modifier.width(48.dp))

        Box(
            modifier = Modifier.weight(1f),
            contentAlignment = Alignment.Center,
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.Center,
                modifier = Modifier.fillMaxWidth(),
            ) {
                Box(
                    modifier = Modifier
                        .scale(if (isYourTurn) pulse else 1f)
                        .size(if (isYourTurn) 10.dp else 8.dp)
                        .background(dotColor, CircleShape),
                )
                Spacer(modifier = Modifier.width(10.dp))
                Text(
                    text = label,
                    color = contentColor,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = if (isYourTurn) FontWeight.SemiBold else FontWeight.Medium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }

        Box(
            modifier = Modifier.width(48.dp).height(40.dp),
            contentAlignment = Alignment.Center,
        ) {
            val nudgeContainerColor =
                if (canNudge) MaterialTheme.colorScheme.tertiaryContainer
                else MaterialTheme.colorScheme.surfaceVariant
            val nudgeContentColor =
                if (canNudge) MaterialTheme.colorScheme.onTertiaryContainer
                else MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.55f)

            IconButton(
                onClick = onNudge,
                enabled = showNudgeAction && canNudge,
                modifier = Modifier
                    .size(40.dp)
                    .alpha(nudgeVisibility)
                    .then(
                        if (showNudgeAction) Modifier
                        else Modifier.clearAndSetSemantics { }
                    )
                    .background(nudgeContainerColor, CircleShape),
            ) {
                Icon(
                    imageVector = BankoIcons.Vibration,
                    contentDescription = nudgeLabel,
                    tint = nudgeContentColor,
                    modifier = Modifier.size(20.dp),
                )
            }
        }
    }
}

@Composable
internal fun DiceSectionWithAnimation(
    game: DomainGame,
    onRollingChange: (Boolean) -> Unit = {},
    dieSize: Dp = DIE_SIZE,
) {
    val twoDimensional by DiceDisplayPreferences.twoDimensional.collectAsState()
    val useThreeDimensionalDice = !twoDimensional
    var diceOnePosition by remember { mutableStateOf(0.dp) }
    var diceTwoPosition by remember { mutableStateOf(0.dp) }
    var diceOneRotation by remember { mutableStateOf(0f) }
    var diceTwoRotation by remember { mutableStateOf(0f) }
    var diceScale by remember { mutableStateOf(1f) }
    var showDice by remember { mutableStateOf(false) }

    // Decelerating tweens: the dice slide/rotate to their spot and ease to a stop — no overshoot.
    val animateDiceScale by animateFloatAsState(
        targetValue = diceScale,
        animationSpec = tween(durationMillis = 400, easing = LinearOutSlowInEasing)
    )

    val animateDiceOnePosition by animateDpAsState(
        targetValue = diceOnePosition,
        animationSpec = tween(durationMillis = 600, easing = LinearOutSlowInEasing)
    )

    val animateDiceTwoPosition by animateDpAsState(
        targetValue = diceTwoPosition,
        animationSpec = tween(durationMillis = 600, easing = LinearOutSlowInEasing)
    )

    // The dice settle to a slightly tilted rest, easing to a stop along with the slide.
    val animateDiceOneRotation by animateFloatAsState(
        targetValue = diceOneRotation,
        animationSpec = tween(durationMillis = 600, easing = LinearOutSlowInEasing)
    )

    val animateDiceTwoRotation by animateFloatAsState(
        targetValue = diceTwoRotation,
        animationSpec = tween(durationMillis = 600, easing = LinearOutSlowInEasing)
    )

    // Entry travel: the dice come in from below (the player tosses them forward/up the table) and
    // decelerate to rest under "friction" — value is a vertical offset in dp, 0 = resting spot.
    val diceEntry = remember { Animatable(0f) }
    // Impact squash on settle: spikes briefly, flattening the die (wider + shorter) then restoring.
    // Not a bounce (no positional overshoot) — a deform that sells the landing.
    val diceSquash = remember { Animatable(0f) }
    val diceRollProgress = remember { Animatable(1f) }

    var displayedDieOne by remember { mutableStateOf(0) }
    var displayedDieTwo by remember { mutableStateOf(0) }
    val dieOneNumber = game.round?.dieOne ?: 0
    val dieTwoNumber = game.round?.dieTwo ?: 0

    // The displayed roll belongs to whoever rolled it (currentPlayer has already advanced), so key
    // the skin off round.rolledBy — not currentPlayer.
    val roller = game.players.firstOrNull { it.name == game.round?.rolledBy }
    val rollerSkin = DiceSkin.fromId(roller?.diceSkin)
    val rollerHat = DiceHat.fromId(roller?.diceHat)

    // Snapshot the skin/hat alongside the displayed dice values (set together in the animation
    // effect below). Deriving them live from round.rolledBy would repaint the still-visible
    // previous roll with the new roller's skin for a frame, before the effect hides the dice.
    var displayedSkin by remember { mutableStateOf(DiceSkin.DEFAULT) }
    var displayedHat by remember { mutableStateOf(DiceHat.NONE) }

    if (displayedDieOne == 0 && displayedDieTwo == 0) {
        // Before the round's first roll the table isn't empty: the dice rest on it, waiting for
        // whoever's turn it is.
        val nextRoller = game.players.firstOrNull { it.name == game.currentPlayer }
        RestingDice(
            dieSize = dieSize,
            skin = DiceSkin.fromId(nextRoller?.diceSkin).style(),
            hat = DiceHat.fromId(nextRoller?.diceHat),
            useThreeDimensionalDice = useThreeDimensionalDice,
        )
    } else {
        DiceSection(
            showDice = showDice,
            dieOneNumber = displayedDieOne,
            dieTwoNumber = displayedDieTwo,
            animateDiceOnePosition = animateDiceOnePosition,
            animateDiceTwoPosition = animateDiceTwoPosition,
            animateDiceScale = animateDiceScale,
            modifier = if (showDice) Modifier else Modifier.alpha(0f),
            skin = displayedSkin.style(),
            hat = displayedHat,
            dieOneRotation = animateDiceOneRotation,
            dieTwoRotation = animateDiceTwoRotation,
            entryOffset = diceEntry.value.dp,
            squash = diceSquash.value,
            dieSize = dieSize,
            rollProgress = diceRollProgress.value,
            useThreeDimensionalDice = useThreeDimensionalDice,
        )
    }

    // Key on the roll identity (round + roll number) rather than the die values so the
    // animation replays on every roll, even when consecutive rolls produce the same pair.
    LaunchedEffect(game.round?.roundNum, game.round?.currentRoll) {
        // A newly started/banked round and a manually entered roll carry 0/0 rather than rendered
        // dice. Previously those states still ran the full hidden animation, which needlessly
        // locked the Roll button and made the first real roll pay a cold-start delay.
        if (!hasRenderableDiceRoll(dieOneNumber, dieTwoNumber)) {
            showDice = false
            displayedDieOne = 0
            displayedDieTwo = 0
            diceEntry.snapTo(0f)
            diceSquash.snapTo(0f)
            diceRollProgress.snapTo(1f)
            onRollingChange(false)
            return@LaunchedEffect
        }

        val hadDisplayedDice = hasRenderableDiceRoll(displayedDieOne, displayedDieTwo)
        onRollingChange(true)
        try {
            showDice = false
            diceScale = 1f
            // Hide dice and reset displayed values before animation
            displayedDieOne = 0
            displayedDieTwo = 0
            // Later rolls get a brief clear-the-table beat. On the first roll there is nothing to
            // clear, so waiting here only looks like input/network lag.
            val resetDelayMillis = diceResetDelayMillis(hadDisplayedDice)
            if (resetDelayMillis > 0) delay(resetDelayMillis)
            diceRollProgress.snapTo(if (useThreeDimensionalDice) 0f else 1f)
            showDice = true
            diceScale = .5f
            // Start the dice below their resting spot so they travel up into the table when revealed
            // (a forward toss from the player's side), and reset any prior squash.
            diceEntry.snapTo(90f)
            diceSquash.snapTo(0f)

            // A tight scatter around center so the two dice read as a thrown pair, not two stray
            // objects at opposite edges.
            val minDistanceApart = 16.dp
            val maxPosition = 20.dp
            val minPosition = (-20).dp

            // Give each die a small random tilt so the pair looks tumbled.
            diceOneRotation = Random.nextInt(-15, 16).toFloat()
            diceTwoRotation = Random.nextInt(-15, 16).toFloat()

            diceOnePosition = Random.nextInt(minPosition.value.toInt(), maxPosition.value.toInt()).dp

            var minPosForDiceTwo = (diceOnePosition + minDistanceApart).coerceAtMost(maxPosition)
            var maxPosForDiceTwo = (diceOnePosition - minDistanceApart).coerceAtLeast(minPosition)

            if (minPosForDiceTwo >= maxPosForDiceTwo) {
                val temp = minPosForDiceTwo
                minPosForDiceTwo = maxPosForDiceTwo
                maxPosForDiceTwo = temp
            }

            diceTwoPosition = Random.nextInt(minPosForDiceTwo.value.toInt(), maxPosForDiceTwo.value.toInt()).dp

            // Reveal the real values as the dice start sliding. The 900ms tumble runs alongside
            // the entry/skid, keeping even a later roll's 500ms reset + tumble under the bot's
            // 1500ms reaction delay. Wait for the tumble before re-enabling the roll button.
            // Apply the roller's skin/hat now (together with the values) so the skin always matches
            // the dice actually on screen.
            displayedSkin = rollerSkin
            displayedHat = rollerHat
            displayedDieOne = dieOneNumber
            displayedDieTwo = dieTwoNumber
            val tumbleJob = if (useThreeDimensionalDice) {
                launch {
                    diceRollProgress.animateTo(
                        targetValue = 1f,
                        animationSpec = tween(
                            durationMillis = DICE_3D_TUMBLE_DURATION_MS,
                            easing = LinearOutSlowInEasing,
                        ),
                    )
                }
            } else null
            // One continuous landing: the dice fly in from below and hit the table partway (the
            // squash fires here, synced to that contact), then keep sliding and decelerate to rest —
            // so the motion ends on a skid, not a dead stop, and the squash is the impact rather
            // than a tacked-on final beat.
            diceEntry.animateTo(
                targetValue = 22f,
                animationSpec = tween(durationMillis = 240, easing = LinearOutSlowInEasing),
            )
            // On contact, nudge each die's tilt a couple degrees so it keeps tipping into place
            // during the skid rather than locking perfectly the instant it touches down.
            diceOneRotation += Random.nextInt(2, 4) * (if (Random.nextBoolean()) 1 else -1)
            diceTwoRotation += Random.nextInt(2, 4) * (if (Random.nextBoolean()) 1 else -1)
            launch {
                // Quick flatten on contact, easing back out while the dice skid the rest of the way.
                diceSquash.animateTo(0.14f, animationSpec = tween(durationMillis = 55))
                diceSquash.animateTo(0f, animationSpec = tween(durationMillis = 210, easing = LinearOutSlowInEasing))
            }
            diceEntry.animateTo(
                targetValue = 0f,
                animationSpec = tween(durationMillis = 380, easing = LinearOutSlowInEasing),
            )
            delay(60)
            tumbleJob?.join()
        } finally {
            onRollingChange(false)
        }
    }
}

internal fun hasRenderableDiceRoll(dieOne: Int, dieTwo: Int): Boolean =
    dieOne in 1..6 && dieTwo in 1..6

internal fun diceResetDelayMillis(hadDisplayedDice: Boolean): Long =
    if (hadDisplayedDice) BETWEEN_ROLL_RESET_DELAY_MS else 0L

private const val BETWEEN_ROLL_RESET_DELAY_MS = 500L

@Composable
private fun GameOverContent(
    game: DomainGame,
    selfName: String,
    compactLandscape: Boolean = false,
    isHost: Boolean = false,
    onRematch: (() -> Unit)? = null,
    onFinishGame: (() -> Unit)? = null,
    modifier: Modifier = Modifier,
) {
    var confettiVisible by remember { mutableStateOf(false) }
    var trophyScale by remember { mutableStateOf(0f) }
    val shareRecap = rememberInviteSharer()
    val recapShareText = remember(game) { buildGameRecapShareText(game) }

    val animatedTrophyScale by animateFloatAsState(
        targetValue = trophyScale,
        animationSpec = tween(durationMillis = 800, easing = LinearEasing)
    )

    LaunchedEffect(Unit) {
        confettiVisible = true
        delay(300)
        trophyScale = 1.2f
        delay(200)
        trophyScale = 1f
    }

    Box(modifier = modifier.fillMaxSize()) {
        // Confetti background
        if (confettiVisible) {
            ConfettiAnimation()
        }

        val gameOverScrollState = rememberScrollState()
        val contentModifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = if (compactLandscape) 12.dp else 20.dp)
            .verticalScroll(gameOverScrollState)
            .padding(bottom = if (compactLandscape) 16.dp else 112.dp)
        Column(
            modifier = contentModifier,
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(if (compactLandscape) 8.dp else 14.dp)
        ) {
            Text(
                text = stringResource(Res.string.game_over_celebration),
                fontFamily = theme.bankoDisplayFontFamily(),
                fontSize = if (compactLandscape) 28.sp else 40.sp,
                color = MaterialTheme.colorScheme.primary,
                textAlign = TextAlign.Center
            )

            val winner = game.players.maxByOrNull { it.points }
            if (winner != null) {
                WinnerHeroCard(
                    name = winner.name,
                    points = winner.points,
                    trophyScale = animatedTrophyScale,
                    compactLandscape = compactLandscape,
                )
            }

            // The story of the game at a glance: how the lead changed hands, round by round.
            val analytics = remember(game) { GameAnalyticsCalculator.from(game) }
            analytics.scoreRace?.let { race ->
                StatsCard {
                    StatsSectionTitle(stringResource(Res.string.stats_section_score_race))
                    ScoreRaceChart(
                        race = race,
                        selfName = selfName,
                        chartHeight = if (compactLandscape) 110.dp else 150.dp,
                    )
                }
            }

            TableRecapCards(
                game = game,
                compactLandscape = compactLandscape,
            )

            Text(
                text = stringResource(Res.string.final_scores),
                fontFamily = theme.bankoDisplayFontFamily(),
                fontSize = 16.sp,
                letterSpacing = 2.sp,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )

            game.players.sortedByDescending { it.points }.forEachIndexed { index, player ->
                ScoreboardRow(
                    rank = index + 1,
                    name = player.name,
                    points = player.points,
                    isWinner = index == 0,
                    compactLandscape = compactLandscape,
                )
            }

            var showGameStats by remember { mutableStateOf(false) }
            GlossySecondaryButton(
                text = stringResource(Res.string.see_full_game_stats),
                onClick = {
                    showGameStats = true
                    Analytics.statsViewed(Analytics.StatsSource.GameOver)
                },
                leadingIcon = BankoIcons.BarChart,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(if (compactLandscape) 48.dp else 52.dp),
            )
            if (showGameStats) {
                GameStatsSheet(
                    game = game,
                    selfName = selfName,
                    onDismiss = { showGameStats = false },
                )
            }

            GlossySecondaryButton(
                text = stringResource(Res.string.share_recap),
                onClick = {
                    Analytics.gameRecapShared()
                    shareRecap(recapShareText)
                },
                leadingIcon = BankoIcons.Share,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(if (compactLandscape) 48.dp else 52.dp),
            )

            // Rematch keeps the whole group together: same players, order, and settings, points
            // zeroed, back to the lobby. Host-only — other clients follow the game doc reset.
            if (isHost && onRematch != null) {
                GlossyPrimaryButton(
                    text = stringResource(Res.string.rematch),
                    onClick = onRematch,
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(if (compactLandscape) 48.dp else 52.dp),
                )
            }

            // The warmest Plus Pass moment: a host whose game just ran on a free pass can renew it
            // with one more ad, and everyone else without Plus learns the pass exists. Hidden for
            // Plus owners and on platforms without rewarded ads.
            val purchaseState by RemoveAdsPurchases.uiState.collectAsState()
            if (BankoRewardedAd.isSupported && !purchaseState.bankoPlusActive) {
                EndGamePlusPassCard(
                    // The host doesn't own Plus yet this game had host perks — the only way that
                    // happens is a spent Plus Pass, so pitch renewal rather than introduction.
                    ranOnPass = isHost && game.hostPerksActive,
                    compactLandscape = compactLandscape,
                )
            }

            // High-intent moment: the player just sat through the end-game interstitial. Offer the
            // matching store view (full ad-free, or Plus for one-time buyers) — secondary styling so
            // it never competes with Finish. Hidden for Plus owners / unsupported platforms.
            val endGameUpsell = storeUpsellFor(purchaseState)
            var showEndStore by remember { mutableStateOf(false) }
            if (endGameUpsell != null) {
                GlossySecondaryButton(
                    text = stringResource(endGameUpsell.labelRes),
                    onClick = { showEndStore = true },
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(if (compactLandscape) 48.dp else 52.dp),
                )
            }
            if (showEndStore && endGameUpsell != null) {
                BankoStoreSheet(
                    onDismiss = { showEndStore = false },
                    plusOnly = endGameUpsell.plusOnly,
                )
            }

            if (compactLandscape && onFinishGame != null) {
                GlossyPrimaryButton(
                    text = stringResource(Res.string.finish_game),
                    onClick = onFinishGame,
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(52.dp),
                )
            }
        }
    }
}

@Composable
private fun TableRecapCards(
    game: DomainGame,
    compactLandscape: Boolean,
) {
    val recap = game.recapStats
    val sevenLeaders = recap.sevenLeaders()
    val cardSpacing = if (compactLandscape) 6.dp else 8.dp

    Text(
        text = stringResource(Res.string.table_recap),
        fontFamily = theme.bankoDisplayFontFamily(),
        fontSize = 16.sp,
        letterSpacing = 2.sp,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
    )

    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(cardSpacing),
    ) {
        RecapMomentCard(
            emoji = "🔥",
            title = stringResource(Res.string.biggest_pot),
            highlighted = recap.biggestPot != null,
            value = recap.biggestPot?.let {
                stringResource(Res.string.points_short_format, it.points)
            } ?: "—",
            detail = recap.biggestPot?.let {
                stringResource(Res.string.round_number, it.roundNum)
            } ?: stringResource(Res.string.no_pot_moment),
            compactLandscape = compactLandscape,
            modifier = Modifier.weight(1f),
        )
        RecapMomentCard(
            emoji = "🏦",
            title = stringResource(Res.string.boldest_bank),
            highlighted = recap.biggestBank != null,
            value = recap.biggestBank?.playerName ?: "—",
            detail = recap.biggestBank?.let {
                stringResource(Res.string.kept_points, it.points)
            } ?: stringResource(Res.string.no_banks_moment),
            compactLandscape = compactLandscape,
            modifier = Modifier.weight(1f),
        )
    }
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(cardSpacing),
    ) {
        RecapMomentCard(
            emoji = "💥",
            title = stringResource(Res.string.biggest_bust),
            highlighted = recap.biggestBust != null,
            value = recap.biggestBust?.playerName ?: "—",
            detail = recap.biggestBust?.let {
                stringResource(Res.string.lost_points, it.points)
            } ?: stringResource(Res.string.no_busts_moment),
            compactLandscape = compactLandscape,
            modifier = Modifier.weight(1f),
        )
        RecapMomentCard(
            emoji = "7️⃣",
            title = stringResource(Res.string.seven_magnet),
            highlighted = sevenLeaders.isNotEmpty(),
            value = sevenLeaders.joinToString(" & ") { it.playerName }.ifBlank { "—" },
            detail = sevenLeaders.firstOrNull()?.let {
                stringResource(Res.string.sevens_count, it.count)
            } ?: stringResource(Res.string.no_sevens_moment),
            compactLandscape = compactLandscape,
            modifier = Modifier.weight(1f),
        )
    }
}

@Composable
private fun RecapMomentCard(
    emoji: String,
    title: String,
    highlighted: Boolean,
    value: String,
    detail: String,
    compactLandscape: Boolean,
    modifier: Modifier = Modifier,
) {
    Surface(
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.surfaceContainerHigh,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        modifier = modifier.heightIn(min = if (compactLandscape) 92.dp else 112.dp),
    ) {
        Column(
            modifier = Modifier.padding(
                horizontal = if (compactLandscape) 10.dp else 12.dp,
                vertical = if (compactLandscape) 8.dp else 11.dp,
            ),
            verticalArrangement = Arrangement.spacedBy(2.dp),
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                Text(text = emoji, fontSize = if (compactLandscape) 17.sp else 20.sp)
                Text(
                    text = title.uppercase(),
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = 0.8.sp,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
            Text(
                text = value,
                style = if (compactLandscape) {
                    MaterialTheme.typography.titleSmall
                } else {
                    MaterialTheme.typography.titleMedium
                },
                fontWeight = FontWeight.Bold,
                color = if (highlighted) {
                    MaterialTheme.colorScheme.primary
                } else {
                    MaterialTheme.colorScheme.onSurface
                },
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Text(
                text = detail,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

/**
 * Game-over Plus Pass offer. Two voices, one card: a host whose game just ran on a spent pass gets
 * "keep it going" (they've felt the perks — the warmest possible renewal moment), everyone else
 * without Plus gets introduced to the pass. Once a pass is pending the button yields to a ready
 * note, so back-to-back games can't stockpile passes.
 */
@Composable
private fun EndGamePlusPassCard(
    ranOnPass: Boolean,
    compactLandscape: Boolean,
) {
    val passPending by PlusPass.pending.collectAsState()
    Surface(
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.surfaceContainerHigh,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        modifier = Modifier.fillMaxWidth(),
    ) {
        Column(
            modifier = Modifier.padding(
                horizontal = 16.dp,
                vertical = if (compactLandscape) 10.dp else 14.dp,
            ),
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            Text(
                text = stringResource(
                    if (ranOnPass) Res.string.plus_pass_endgame_renew_title
                    else Res.string.plus_pass_title
                ),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.onSurface,
            )
            Text(
                text = stringResource(
                    if (ranOnPass) Res.string.plus_pass_endgame_renew_body
                    else Res.string.plus_pass_body
                ),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            if (passPending) {
                Text(
                    text = stringResource(Res.string.plus_pass_ready),
                    style = MaterialTheme.typography.labelLarge,
                    color = MaterialTheme.colorScheme.primary,
                )
            } else {
                WatchAdButton(onRewarded = {
                    PlusPass.grant()
                    Analytics.plusPassEarned()
                })
            }
        }
    }
}

/**
 * The winner's podium: a glossy gold "coin" card (the same struck-coin treatment as the pot) with a
 * springy trophy, the winner's name in the display font, and their score — the celebratory hero of
 * the game-over screen.
 */
@Composable
private fun WinnerHeroCard(
    name: String,
    points: Int,
    trophyScale: Float,
    compactLandscape: Boolean = false,
) {
    val cardShape = RoundedCornerShape(if (compactLandscape) 18.dp else 24.dp)
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .shadow(
                elevation = 14.dp,
                shape = cardShape,
                spotColor = MaterialTheme.colorScheme.primary,
                ambientColor = MaterialTheme.colorScheme.primary,
            )
            .clip(cardShape)
            .background(MaterialTheme.colorScheme.primaryContainer),
    ) {
        Box(
            Modifier
                .matchParentSize()
                .background(
                    brush = Brush.verticalGradient(
                        0f to Color.White.copy(alpha = 0.40f),
                        0.5f to Color.Transparent,
                        1f to Color.Black.copy(alpha = 0.08f),
                    ),
                    shape = cardShape,
                ),
        )
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(
                    vertical = if (compactLandscape) 10.dp else 20.dp,
                    horizontal = 16.dp,
                ),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            Icon(
                imageVector = BankoIcons.EmojiEvents,
                contentDescription = null,
                tint = Color(0xFFFFC107),
                modifier = Modifier.size(if (compactLandscape) 44.dp else 72.dp).scale(trophyScale),
            )
            Text(
                text = "WINNER",
                fontFamily = theme.bankoDisplayFontFamily(),
                fontSize = if (compactLandscape) 12.sp else 14.sp,
                letterSpacing = 3.sp,
                color = MaterialTheme.colorScheme.onPrimaryContainer.copy(alpha = 0.8f),
            )
            Text(
                text = name,
                fontFamily = theme.bankoDisplayFontFamily(),
                fontSize = if (compactLandscape) 26.sp else 34.sp,
                color = MaterialTheme.colorScheme.onPrimaryContainer,
                textAlign = TextAlign.Center,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Text(
                text = stringResource(Res.string.points_format, points),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.onPrimaryContainer.copy(alpha = 0.9f),
            )
        }
    }
}

/** One row of the final scoreboard, styled like the in-game player cards (raised + rimmed); the
 * winner is filled with primary, the rest are tonal, with gold/silver/bronze medals for the top 3. */
@Composable
private fun ScoreboardRow(
    rank: Int,
    name: String,
    points: Int,
    isWinner: Boolean,
    compactLandscape: Boolean = false,
) {
    val container = if (isWinner) {
        MaterialTheme.colorScheme.primary
    } else {
        MaterialTheme.colorScheme.surfaceContainerHigh
    }
    val onContainer = if (isWinner) {
        MaterialTheme.colorScheme.onPrimary
    } else {
        MaterialTheme.colorScheme.onSurfaceVariant
    }
    val medalTint = when (rank) {
        1 -> Color(0xFFFFC107)
        2 -> Color(0xFFB0BEC5)
        3 -> Color(0xFFCD7F32)
        else -> null
    }
    Surface(
        shape = RoundedCornerShape(14.dp),
        color = container,
        shadowElevation = if (isWinner) 4.dp else 2.dp,
        border = if (isWinner) null else BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        modifier = Modifier.fillMaxWidth(),
    ) {
        Row(
            modifier = Modifier.padding(
                horizontal = 14.dp,
                vertical = if (compactLandscape) 7.dp else 10.dp,
            ),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(modifier = Modifier.width(32.dp), contentAlignment = Alignment.Center) {
                if (medalTint != null) {
                    Icon(
                        imageVector = BankoIcons.EmojiEvents,
                        contentDescription = null,
                        tint = medalTint,
                        modifier = Modifier.size(if (compactLandscape) 18.dp else 22.dp),
                    )
                } else {
                    Text(text = "$rank", fontSize = 15.sp, fontWeight = FontWeight.SemiBold, color = onContainer)
                }
            }
            Spacer(modifier = Modifier.width(10.dp))
            Text(
                text = name,
                style = if (compactLandscape) MaterialTheme.typography.bodyMedium else MaterialTheme.typography.bodyLarge,
                fontWeight = if (isWinner) FontWeight.Bold else FontWeight.SemiBold,
                color = onContainer,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f),
            )
            Text(
                text = stringResource(Res.string.points_short_format, points),
                style = if (compactLandscape) MaterialTheme.typography.bodyMedium else MaterialTheme.typography.bodyLarge,
                fontWeight = FontWeight.Bold,
                color = onContainer,
            )
        }
    }
}

@Composable
private fun ConfettiAnimation() {
    val confettiPieces = remember {
        (1..30).map {
            ConfettiPiece(
                x = Random.nextFloat(),
                y = -0.1f,
                color = listOf(
                    Color(0xFFFFD700), // Gold
                    Color(0xFFFF6B6B), // Red
                    Color(0xFF4ECDC4), // Teal
                    Color(0xFF45B7D1), // Blue
                    Color(0xFF96CEB4), // Green
                    Color(0xFFFECA57)  // Orange
                ).random(),
                size = Random.nextFloat() * 8 + 4,
                rotationSpeed = Random.nextFloat() * 10 - 5,
                fallSpeed = Random.nextFloat() * 2 + 1
            )
        }
    }

    var animationProgress by remember { mutableStateOf(0f) }

    LaunchedEffect(Unit) {
        while (true) {
            animationProgress += 0.016f // ~60fps
            if (animationProgress > 5f) animationProgress = 0f
            delay(16)
        }
    }

    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val screenWidth = maxWidth
        val screenHeight = maxHeight

        confettiPieces.forEach { piece ->
            val currentY = piece.y + (piece.fallSpeed * animationProgress) % 1.2f
            if (currentY <= 1f) {
                Box(
                    modifier = Modifier
                        .offset(
                            x = piece.x * screenWidth,
                            y = currentY * screenHeight
                        )
                        .size(piece.size.dp)
                        .background(
                            piece.color,
                            RoundedCornerShape(2.dp)
                        )
                        .scale(0.8f + 0.4f * sin(piece.rotationSpeed * animationProgress))
                )
            }
        }
    }
}

private data class ConfettiPiece(
    val x: Float,
    val y: Float,
    val color: Color,
    val size: Float,
    val rotationSpeed: Float,
    val fallSpeed: Float
)
